/**
 * ExpressionControl — the `expression` form control (Plan/05 §4, "Option B").
 *
 * A field whose value is *always* authored through Expressions Studio's modal editor: a compact
 * read-only preview of the current wildcard text plus an "Edit using Expression Builder" trigger
 * that opens the full-screen editor, seeded from the current value and written back to the form
 * **only** on explicit OK. Structurally this is `document-merge`'s `ContentField.tsx` ported into
 * the Atlas Forms control contract; both halves are the shared components from
 * `@bizfirst/expressions-designer`, not local re-implementations.
 *
 * ── How this reaches production, and the one thing that would break it ──────────────────────────
 *
 * `FormRenderer` renders through `FormField.tsx`, whose ~3400-line `switch (control.type)` has
 * hand-written inline JSX for most built-in types and only consults `ReactControlRegistry` in its
 * `default:` branch. `'expression'` deliberately has **no case in that switch**, so it lands on the
 * `default:` → registry path — the same path `form-picker` is reached through in production today.
 *
 * **Never add a `case 'expression':` to `FormField.tsx`.** Doing so shadows this component and
 * silently turns it into dead code, which is exactly what already happened to `color-picker`,
 * `rich-text-editor` and `code-editor` (and to `text`/`textarea`'s registered components, the
 * discovery that killed Plan/05's Option A). `ExpressionControl.formrenderer.test.tsx` asserts the
 * absence of that case against `FormField.tsx`'s own source, and asserts that a real schema rendered
 * through the real `FormRenderer` mounts *this* component.
 *
 * ── Props contract ─────────────────────────────────────────────────────────────────────────────
 *
 * Built strictly against `ControlRendererProps` (the current contract — nested `control` object,
 * plus `error`/`touched`/`mode`), not the flat legacy `FormControlProps` that `FormPickerComponent`
 * and several "Phase 3" controls use; the real host only ever passes the nested shape, so a control
 * built against the flat one silently never receives `label`/`required`/`helpText`.
 *
 * `ExpressionControlProps` below restates that contract rather than importing it, and that is a
 * build constraint, not a preference: the workspace's root `tsconfig.json` maps `@atlas-forms/*` to
 * `packages/&#42;/src`, so importing anything from `@atlas-forms/player-components-react` here drags that
 * package's entire source tree (plus `storage-js`, `themes-js`, …) into this `composite: true`
 * project and fails with ~40 TS6059/TS6307 `rootDir` errors. Adding a project reference cannot fix
 * it either — `player-components-react → designer-components-react → controls-advanced-react` is
 * already a cycle, and TypeScript forbids cycles between composite projects. The restatement is
 * held honest by a compile-time conformance assertion against the real `ControlRendererProps` in
 * `src/__tests__/ExpressionControl.formrenderer.test.tsx` (test sources are outside this
 * tsconfig's `include`, so they can import the player package freely).
 *
 * ── Label / error ownership ────────────────────────────────────────────────────────────────────
 *
 * Plan/05 §4's sketch renders `control.label` and the error message inside the control. That would
 * render both **twice** in production: `FormField` already wraps every `default:`-branch component
 * in its own `<label htmlFor={control.id}>`, description, help text and error paragraph. So this
 * component renders neither, and instead reflects `error`/`touched` as `data-invalid`/`aria-invalid`
 * on its root for styling and assistive tech. Deviation from the doc, recorded deliberately.
 *
 * ── Lazy loading ───────────────────────────────────────────────────────────────────────────────
 *
 * `@bizfirst/expressions-designer` drags in four `@tiptap/*` packages and the whole Expressions
 * Studio package chain. Everything from it is behind `React.lazy` + `Suspense`, so importing
 * `@atlas-forms/controls-advanced-react` — or rendering any of its other 20-odd controls — never
 * pulls the editor into a consumer's bundle. Only rendering an `expression` field does.
 *
 * This is a deliberate departure from `CodeEditorControl`/`RichTextEditorControl`, whose comments
 * say "lazy-load" but which actually `require()` Ace and Quill at **module scope** inside a
 * try/catch — that is optional-dependency tolerance, not laziness, and it puts those libraries in
 * every consumer's bundle unconditionally. `React.lazy` is already the established idiom for this
 * in the codebase proper: `FormField.tsx` itself lazy-loads `FormContainerControl` and `GridControl`
 * that way.
 *
 * `Suspense` alone is not enough to make that safe, though: it catches the *pending* state of a lazy
 * import, never a rejected one. `ExpressionControlErrorBoundary` handles the rejection — see its
 * header — so a chunk that fails to arrive costs the field its editor, not its existence.
 *
 * ── Sanitisation: deliberately none, and here is why ────────────────────────────────────────────
 *
 * `onConfirm` writes the confirmed string to `onChange` **verbatim**, with no sanitiser in between.
 * That is the opposite of what Plan/05 §3B/§3C do for the opt-in trigger on `text`/`textarea`/
 * `rich-text-editor`, where the confirmed value is routed through `FormField`'s own
 * `sanitizeFieldValue` — so the difference is worth stating rather than leaving for the next reader
 * to wonder about.
 *
 * Three reasons, in order of weight:
 *
 *   1. **It is the framework's baseline for this entire class of control, not a carve-out for this
 *      one.** `FormField.tsx`'s `default:` branch — the *only* path any registry-driven control is
 *      reached through — constructs every custom component with `onChange={onChange ?? (() => {})}`:
 *      the form's raw setter, unwrapped. `form-picker` and every other registered control receives
 *      the same unsanitised handler. §3B/§3C sanitise not because registered controls are expected
 *      to, but because their values land in `FormField`'s own inline `<input>`/`<textarea>`, whose
 *      *typed* path already sanitises; parity with typing was the requirement there. There is no
 *      typed path here to be at parity with.
 *   2. **The value is a template, not prose.** Its wildcard delimiters (`%{…}`, `@{…}`, `{{…}}`) and
 *      `directive:key` payloads are load-bearing syntax that a downstream resolver parses.
 *      `sanitizeInput`'s HTML-entity encoding would mangle exactly the characters the syntax is made
 *      of, and an `outputFormat: 'html'` field — this control's default — stores real markup by
 *      design; encoding it here would store the tags rather than the formatting.
 *   3. **Nothing downstream renders it as HTML unescaped.** Verified across the `default:`-branch
 *      path: the value goes into form state and back out through this control's own preview, and
 *      every renderer in this codebase that does interpret a value as markup (`HtmlControl`,
 *      `ArticleControl`) runs its own DOMPurify pass on it. Escaping is the responsibility of
 *      whatever finally renders the resolved template, which is where it has to live anyway — the
 *      resolver substitutes untrusted data into this string *after* it leaves the form.
 *
 * If a future host renders a stored `expression` value as raw HTML without its own sanitisation,
 * that host has the bug, and the fix belongs there — not here, where it would break the syntax this
 * control exists to author.
 */

import React, { Suspense, useCallback, useMemo } from 'react';
import type { FormControl, OperatingMode } from '@atlas-forms/types-js';
import type { EditorOutputFormat, WildcardSyntax } from '@bizfirst/expressions-types';
import {
  useExpressionControlDataSources,
  EXPRESSION_CONTROL_DEFAULT_FORMAT,
  EXPRESSION_CONTROL_DEFAULT_SYNTAX,
} from './expressionControlRuntime';
import { ExpressionControlErrorBoundary } from './ExpressionControlErrorBoundary';

/**
 * The design-time `control.config` shape, exactly the two keys Plan/05 §4 names. Both optional:
 * a schema author who sets neither gets the defaults below, and the design-time panel
 * (`ExpressionEditor` in `@atlas-forms/designer-components-react`) writes the same two keys.
 */
export interface ExpressionControlConfig {
  /** Delimiter style the stored value is written in. Default `EXPRESSION_CONTROL_DEFAULT_SYNTAX`. */
  syntax?: WildcardSyntax;
  /**
   * `'html'` keeps formatting marks; `'text'` flattens to plain text. Default
   * `EXPRESSION_CONTROL_DEFAULT_FORMAT`.
   */
  outputFormat?: EditorOutputFormat;
}

/**
 * Exactly the `ControlRendererProps` members this control consumes, member for member (see the
 * header note for why this is restated rather than imported). `control` is the nested control
 * object — the *only* thing `FormField`'s `default:` branch and `ControlRenderer` ever pass — which
 * is what separates this from the flat legacy `FormControlProps`.
 */
export interface ExpressionControlProps {
  control: FormControl;
  value: any;
  onChange: (value: any) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  mode?: OperatingMode;
}

/*
 * The two design-time defaults live in `./expressionControlRuntime` rather than here, so
 * `@atlas-forms/designer-components-react`'s `ExpressionEditor` can import the same literals for
 * its "(default)" markers instead of restating them. See those constants for why the syntax one is
 * a second copy of the player package's canonical `EXPRESSION_DEFAULT_SYNTAX` rather than an
 * import of it — a `composite`-project constraint, not a choice — and how that copy is held honest.
 */

const LazyExpressionsBuilderLauncher = React.lazy(async () => {
  const mod = await import('@bizfirst/expressions-designer');
  return { default: mod.ExpressionsBuilderLauncher };
});

const LazyExpressionsValuePreview = React.lazy(async () => {
  const mod = await import('@bizfirst/expressions-designer');
  return { default: mod.ExpressionsValuePreview };
});

export function ExpressionControl({
  control,
  value,
  onChange,
  onBlur,
  error,
  touched,
  disabled,
  mode,
}: ExpressionControlProps): React.ReactElement {
  const config = (control.config ?? {}) as ExpressionControlConfig;
  const syntax = config.syntax ?? EXPRESSION_CONTROL_DEFAULT_SYNTAX;
  const outputFormat = config.outputFormat ?? EXPRESSION_CONTROL_DEFAULT_FORMAT;

  const stringValue = typeof value === 'string' ? value : value == null ? '' : String(value);
  /**
   * `mode === 'view'` is kept for the `ControlRenderer`-direct path, but note that it is
   * unreachable through `FormRenderer`: `FormField.tsx` short-circuits view mode ~1700 lines
   * before its `default:` branch and renders a plain read-only value box for every type outside a
   * hardcoded allowlist (`form-container`, `grid`, charts, `image`, …). No registered control is
   * reached in view mode. That is a pre-existing host behaviour, not something this control can
   * opt out of — recorded here because Plan/05 §4's sketch assumes otherwise.
   */
  const isReadOnly = mode === 'view' || control.readonly === true;
  const isDisabled = disabled === true || control.disabled === true;
  const showError = Boolean(touched && error);

  /**
   * Subscribed, not read: a host that calls `configureExpressionControl()` asynchronously — after
   * login, after tenant resolution — must reach fields that are already mounted. Reading the
   * singleton directly during render left those fields showing the "not configured" notice
   * permanently, which for this control means a value that cannot be edited at all.
   */
  const dataSources = useExpressionControlDataSources();

  const editorProps = useMemo(
    () => ({ syntax, outputFormat, dataSources }),
    [syntax, outputFormat, dataSources],
  );

  /**
   * Fires only on explicit OK — `ExpressionsBuilderLauncher` guarantees that, so "nothing happens
   * to my field unless I click OK" is a property of the component rather than of convention.
   * `onBlur` is called after so the form marks the field touched and revalidates, the same way a
   * native input's blur would.
   */
  const handleConfirm = useCallback(
    (next: string): void => {
      onChange(next);
      onBlur?.();
    },
    [onChange, onBlur],
  );

  const rootProps = {
    className: 'af-expression-control',
    'data-testid': 'atlas-expression-control',
    'data-control-id': control.id,
    'data-control-type': 'expression',
    'data-invalid': showError ? 'true' : undefined,
    'aria-invalid': showError ? ('true' as const) : undefined,
  };

  /**
   * The one shape every "there is no editor right now" state renders: an explanation plus the raw
   * stored value, so a value authored earlier is never silently hidden by a condition the user
   * cannot see. Factored out so the unconfigured-host branch and the failed-chunk fallback below
   * cannot drift apart — the review found the latter bypassing the former entirely and showing a
   * raw error string instead.
   */
  const renderNotice = (state: 'unconfigured' | 'unavailable', notice: React.ReactNode) => (
    <div {...rootProps} data-state={state}>
      <div className="af-expression-control__notice" role="status">
        {notice}
      </div>
      {stringValue ? (
        <pre className="af-expression-control__raw" data-testid="atlas-expression-raw-value">
          {stringValue}
        </pre>
      ) : null}
    </div>
  );

  /**
   * `expression` is a field's *only* editor — unlike an opt-in enhancement on a textbox there is no
   * native input underneath to fall back to. So an unconfigured host gets an explicit, visible
   * explanation rather than a blank space or a crash, because a silently empty field is
   * indistinguishable from a broken form and leaves the value permanently uneditable.
   */
  if (!dataSources) {
    return renderNotice(
      'unconfigured',
      <>
        <strong>Expression Builder is not configured for this app.</strong>{' '}
        Call <code>configureExpressionControl(dataSources)</code> from
        <code> @atlas-forms/controls-advanced-react</code> during startup to supply the wildcard
        catalog this field edits against.
      </>,
    );
  }

  return (
    /*
      The boundary wraps the root element rather than sitting inside it, so that on failure the
      fallback *replaces* the whole control — one root, one `data-testid`, one `data-state`. Nesting
      it inside would have produced two `atlas-expression-control` roots, the outer one still
      claiming `data-state="ready"` while the inner one said otherwise.

      And it sits outside the `Suspense`, not inside: `Suspense` handles the chunk being *slow*,
      this handles the chunk never arriving. Without it a rejected import unwinds all the way to
      `FormRenderer`'s `FieldErrorBoundary` and replaces the entire field with a generic
      "Field error", throwing away both this control's own messaging and the stored value.
    */
    <ExpressionControlErrorBoundary
      fallback={renderNotice(
        'unavailable',
        <>
          <strong>The Expression Builder could not be loaded.</strong>{' '}
          Its editor failed to download — usually a transient network or deployment problem.
          Reload the page to try again; the value below is unchanged and still saved.
        </>,
      )}
    >
      <div {...rootProps} data-state="ready">
        <Suspense
          fallback={
            <div className="af-expression-control__loading" data-testid="atlas-expression-loading">
              Loading Expression Builder…
            </div>
          }
        >
          {/*
            §3.1's collapsed read-only box: bracketed wildcard labels, never resolved sample values
            and never raw `directive:key` syntax. Shared component, not a local re-implementation.
            `resolve` is deliberately not wired — without a live mention registry the box degrades to
            derived labels, which is correct behaviour, not a gap.
          */}
          <LazyExpressionsValuePreview value={stringValue} syntax={syntax} outputFormat={outputFormat} />

          <div className="af-expression-control__trigger-row">
            <LazyExpressionsBuilderLauncher
              // Stable per field, never per open: it is the key an opted-in host publishes
              // `window.wildcard.sources[instanceID]` under, and the launcher already remounts its
              // editor per session for isolation.
              instanceID={control.id}
              initialValue={stringValue}
              title={control.label ? `Edit ${control.label}` : undefined}
              disabled={isReadOnly || isDisabled}
              // Written verbatim, with no sanitiser in between — see this file's header for the
              // full reasoning. In short: unsanitised `onChange` is the framework's baseline for
              // every registry-driven control, the wildcard template syntax must survive intact,
              // and downstream renderers own their own escaping.
              onConfirm={handleConfirm}
              editorProps={editorProps}
            />
          </div>
        </Suspense>
      </div>
    </ExpressionControlErrorBoundary>
  );
}

export default ExpressionControl;
